import { injectable } from 'inversify'
import type { DomainService } from '../../DomainHandling/DomainService'
import { GeoLocationService } from '../../Geolocation/GeoLocationService'
import { extractObjectIdFromPath } from '../../Lots/extractObjectIdFromUri'
import { extractAuthenticationCookie } from '../../WebAuth/extractAuthenticationCookie'
import { LotApiRequest } from '../Models/LotApiRequest'
import { LotApiResult } from '../Models/LotApiResult'
import { languageCodeMapping } from '../Settings/LanguageCodeMapping'
import type { LotFetchService } from './LotFetchService'
import { LotFromHttpRequest } from './LotFromHttpRequest'
import { LotService } from './LotService'

@injectable()
export class LotServiceWithCaching implements LotService {
  private cachedLotApiResult: LotApiResult | null

  constructor(
    private lotFetchService: LotFetchService,
    private geoLocationService: GeoLocationService,
    private domainService: DomainService
  ) {
    this.cachedLotApiResult = null
  }

  async getLot(request: LotApiRequest): Promise<LotApiResult> {
    if (this.cachedLotApiResult === null) {
      const rawApiResult = await this.lotFetchService.getLot(request)
      const domainReplacedResult = this.domainService.replaceDomainsFromObject(
        rawApiResult as unknown as { [key: string]: unknown }
      )
      this.cachedLotApiResult = domainReplacedResult as LotApiResult
    }
    return this.cachedLotApiResult
  }

  async getLotFromHttpRequest(request: LotFromHttpRequest): Promise<LotApiResult> {
    const lotId = extractObjectIdFromPath(request.url)

    if (!lotId) {
      throw 'LotDataExtender.process - LOT ID missing!'
    }

    const languageMapping = languageCodeMapping.find((x) => x.sitecore == request.sitecore.context.language)
    if (!languageMapping?.lotAPI)
      throw `Missing value of languageCodeMapping.lotAPI for ${request.sitecore.context.language}`
    const languageCode = languageMapping.lotAPI

    const geoModel = this.geoLocationService.getGeoLocation(request.headers)
    const geoCountryCode = geoModel != null ? geoModel.countryCode : ''

    const authenticationCookie = extractAuthenticationCookie(request.cookie)

    if (this.cachedLotApiResult === null) {
      const lotApiRequest = {
        objectId: lotId,
        languageId: languageCode ?? '',
        authenticationCookie: authenticationCookie,
        geoLocation: geoCountryCode,
      }
      const rawApiResult = await this.lotFetchService.getLot(lotApiRequest)
      const domainReplacedResult = this.domainService.replaceDomainsFromObject(
        rawApiResult as unknown as { [key: string]: unknown }
      )
      this.cachedLotApiResult = domainReplacedResult as LotApiResult
    }
    return this.cachedLotApiResult
  }
}

//  DON'T DELETE - used by DW
